import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import ConfirmDialog from './ConfirmDialog';
import { Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from './Navbar';
import PropertyBasicInfo from './property/PropertyBasicInfo';
import TenantInfo from './property/TenantInfo';
import UtilitiesSection from './property/UtilitiesSection';
import DocumentsSection from './property/DocumentsSection';
import { API, calculateDaysUntilExpiry, getDocumentLabel } from './property/utils';

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Smart back: respect the page that linked here (Dashboard / Renewals),
  // otherwise fall back to the Properties list.
  const ALLOWED_BACK = {
    '/dashboard': 'Dashboard',
    '/renewals': 'Renewals',
    '/properties': 'Properties',
  };
  const fromPath = location.state && location.state.from;
  const backTo = ALLOWED_BACK[fromPath] ? fromPath : '/properties';
  const backLabel = ALLOWED_BACK[backTo];
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docOpenRequest, setDocOpenRequest] = useState(null);

  // Build the urgent-items summary for the banner. Includes the tenancy_end
  // date itself plus any per-document expiries <= 30 days (excluding
  // tenancy_agreement since its expiry mirrors tenancy_end — same dedup as
  // the Renewals page).
  const buildUrgentItems = () => {
    if (!property) return [];
    const items = [];
    const tDays = calculateDaysUntilExpiry(property.tenancy_end_date);
    if (tDays !== null && tDays <= 30) {
      items.push({
        kind: 'tenancy',
        docType: null,
        label: 'Tenancy ends',
        days: tDays,
      });
    }
    // Latest-per-type so a replaced doc doesn't trigger a stale alert.
    const latestByType = new Map();
    documents.forEach((d) => {
      if (d.document_type === 'tenancy_agreement') return;
      const prev = latestByType.get(d.document_type);
      if (!prev || (d.uploaded_at || '') > (prev.uploaded_at || '')) {
        latestByType.set(d.document_type, d);
      }
    });
    latestByType.forEach((d) => {
      const days = calculateDaysUntilExpiry(d.expiry_date);
      if (days !== null && days <= 30) {
        items.push({
          kind: 'document',
          docType: d.document_type,
          label: `${getDocumentLabel(d.document_type)} expires`,
          days,
        });
      }
    });
    items.sort((a, b) => a.days - b.days);
    return items;
  };

  const handleBannerItemClick = (item) => {
    if (item.kind === 'tenancy') {
      // Tenancy_end has no specific document panel — scroll to the Documents
      // section where renewal items are highlighted.
      const node = document.querySelector('.documents-section');
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // Ask DocumentsSection to expand the doc and scroll itself into view.
    setDocOpenRequest({ docType: item.docType, stamp: Date.now() });
  };

  useEffect(() => {
    fetchPropertyDetails();
  }, [id]); // eslint-disable-line

  const fetchPropertyDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const [propResponse, docsResponse] = await Promise.all([
        axios.get(`${API}/properties/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/properties/${id}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setProperty(propResponse.data);
      setDocuments(docsResponse.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load property details');
      setLoading(false);
    }
  };

  const handleDeleteProperty = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property deleted successfully');
      navigate('/dashboard', { state: { activeTab: 'properties' } });
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading property...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="error-screen">
        <p>Property not found</p>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const urgentItems = buildUrgentItems();
  const mostUrgent = urgentItems[0];
  const bannerSeverity = mostUrgent && mostUrgent.days < 15 ? 'urgent' : 'warning';

  return (
    <div className="property-detail-page app-page">
      <Navbar back={{ to: backTo, label: backLabel }} />

      <div className="property-detail-container">
        <div className="property-title-row">
          <h2 className="property-main-title" data-testid="property-title">{property.address}</h2>
          <div className="property-title-actions">
            <Button onClick={() => setDeleteDialogOpen(true)} className="btn-danger" data-testid="delete-property-button">
              <Trash2 size={18} />
              Delete Property
            </Button>
          </div>
        </div>

        {urgentItems.length > 0 && (
          <button
            type="button"
            className={`urgent-banner urgent-banner-${bannerSeverity}`}
            onClick={() => handleBannerItemClick(mostUrgent)}
            data-testid="urgent-banner"
            aria-label={`${urgentItems.length} urgent item${urgentItems.length === 1 ? '' : 's'}`}
          >
            <AlertTriangle size={18} />
            <span className="urgent-banner-text">
              <strong>
                {urgentItems.length} urgent item{urgentItems.length === 1 ? '' : 's'}
              </strong>
              <span className="urgent-banner-dash"> — </span>
              {mostUrgent.label} {mostUrgent.days < 0
                ? `${Math.abs(mostUrgent.days)} day${Math.abs(mostUrgent.days) === 1 ? '' : 's'} ago`
                : `in ${mostUrgent.days} day${mostUrgent.days === 1 ? '' : 's'}`}
              {urgentItems.length > 1 && (
                <span className="urgent-banner-more"> · +{urgentItems.length - 1} more</span>
              )}
            </span>
          </button>
        )}

        <PropertyBasicInfo
          property={property}
          propertyId={id}
          onPropertyUpdated={fetchPropertyDetails}
        />

        <TenantInfo
          property={property}
          propertyId={id}
          documents={documents}
          onTenantUpdated={fetchPropertyDetails}
        />

        <UtilitiesSection
          property={property}
          propertyId={id}
          onUtilityUpdated={fetchPropertyDetails}
        />

        <DocumentsSection
          property={property}
          documents={documents}
          propertyId={id}
          onDocumentsChanged={fetchPropertyDetails}
          openDocumentRequest={docOpenRequest}
        />
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Property"
        description={`Are you sure you want to delete ${property.address}? This will also delete all associated documents and cannot be undone.`}
        onConfirm={handleDeleteProperty}
        confirmText="Delete"
      />
    </div>
  );
}
